import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../../components/ui/Sidebar";
import Sidebar from "../../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import PurchaseRefundsTable, {
  PurchaseCreditNotesTable,
} from "./components/PurchaseRefundsTables";
import { useGetPurchaseRefunds } from "./useGetPurchaseRefunds";
import { useGetPurchaseCreditNotes } from "./useGetPurchaseCreditNotes";
import { useVoidPurchaseRefund } from "./useVoidPurchaseRefund";
import { useVoidPurchaseCreditNote } from "./useVoidPurchaseCreditNote";
import toast from "react-hot-toast";

const TABS = [
  { id: "refunds", label: "Refunds", icon: "RotateCcw" },
  { id: "credit-notes", label: "Credit Notes", icon: "FileMinus" },
];

const PurchaseRefundsContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");
  const navigate = useNavigate();

  const { data: refundsData = [], isLoading: refundsLoading } =
    useGetPurchaseRefunds();
  const { data: creditNotesData = [], isLoading: creditNotesLoading } =
    useGetPurchaseCreditNotes();

  const [activeTab, setActiveTab] = useState("refunds");
  const [refunds, setRefunds] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (refundsData.length) setRefunds(refundsData);
  }, [refundsData]);
  useEffect(() => {
    if (creditNotesData.length) setCreditNotes(creditNotesData);
  }, [creditNotesData]);
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm("");
  }, [activeTab]);

  const activeList = activeTab === "refunds" ? refunds : creditNotes;

  const filtered = activeList.filter((item) => {
    if (!searchTerm.trim()) return true;
    const t = searchTerm.toLowerCase();
    return (
      item.vendorName?.toLowerCase().includes(t) ||
      item.billNumber?.toLowerCase().includes(t) ||
      (item.refundNumber || item.creditNoteNumber || "")
        .toLowerCase()
        .includes(t)
    );
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const voidRefund = useVoidPurchaseRefund();
  const voidCreditNote = useVoidPurchaseCreditNote();

  const handleDeleteRefund = (id) =>
    voidRefund.mutate(
      { id, reason: "Voided from refunds list" },
      {
        onSuccess: () => toast.success("Refund voided"),
        onError: (err) => toast.error(err?.message || "Failed to void refund"),
      },
    );
  const handleDeleteCreditNote = (id) =>
    voidCreditNote.mutate(
      { id, reason: "Voided from credit notes list" },
      {
        onSuccess: () => toast.success("Credit note voided"),
        onError: (err) =>
          toast.error(err?.message || "Failed to void credit note"),
      },
    );

  // Stats
  const totalRefundAmount = refunds.reduce(
    (s, r) => s + (parseFloat(r.netAmount) || 0),
    0,
  );
  const totalCreditNoteAmount = creditNotes.reduce(
    (s, c) => s + (parseFloat(c.netAmount) || 0),
    0,
  );

  const isLoading = refundsLoading || creditNotesLoading;
  if (isLoading) return <LoadingSkeleton isCollapsed={isCollapsed} />;

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <main className="p-4 md:p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                {t("purchaseRefunds.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("purchaseRefunds.description")}
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" size="md" iconName="Download">
                {t("actions.export")}
              </Button>
              {activeTab === "refunds" ? (
                <Button
                  variant="default"
                  size="md"
                  iconName="Plus"
                  onClick={() => navigate("/purchases/refunds/new")}
                >
                  {t("purchaseRefunds.addRefund")}
                </Button>
              ) : (
                <Button
                  variant="default"
                  size="md"
                  iconName="Plus"
                  onClick={() => navigate("/purchases/credit-notes/new")}
                >
                  {t("purchaseRefunds.addCreditNote")}
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {[
            {
              label: t("refunds.stats.totalRefunds"),
              value: refunds.length,
              icon: "RotateCcw",
              color:
                "text-green-500 bg-green-100 dark:bg-green-900/20 dark:text-green-400",
            },
            {
              label: t("refunds.stats.totalRefunded"),
              value: `SR ${totalRefundAmount.toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
              icon: "TrendingUp",
              color:
                "text-blue-500 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
            },
            {
              label: t("refunds.stats.creditNotes"),
              value: creditNotes.length,
              icon: "FileMinus",
              color:
                "text-violet-500 bg-violet-100 dark:bg-violet-900/20 dark:text-violet-400",
            },
            {
              label: t("refunds.stats.totalCreditValue"),
              value: `SR ${totalCreditNoteAmount.toLocaleString("en-SA", { minimumFractionDigits: 2 })}`,
              icon: "BadgeMinus",
              color:
                "text-orange-500 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400",
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-card rounded-xl p-4 shadow-sm border border-border flex items-center gap-3"
            >
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${stat.color}`}
              >
                <Icon name={stat.icon} size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-xl font-bold text-foreground truncate">
                  {stat.value}
                </p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="mb-6"
        >
          <div className="flex gap-1 bg-muted p-1 rounded-xl w-fit border border-border">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? "bg-card text-foreground shadow-sm border border-border"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon name={tab.icon} size={15} />
                {tab.id === "refunds"
                  ? t("status.refunds")
                  : t("status.creditNotes")}
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    activeTab === tab.id
                      ? "bg-primary/10 text-primary"
                      : "bg-muted-foreground/20 text-muted-foreground"
                  }`}
                >
                  {tab.id === "refunds" ? refunds.length : creditNotes.length}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-card rounded-xl p-5 shadow-sm border border-border mb-6"
        >
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={
                activeTab === "refunds"
                  ? t("purchaseRefunds.searchRefunds")
                  : t("purchaseRefunds.searchCreditNotes")
              }
              className="w-full ps-10 pe-10 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={15} />
              </button>
            )}
          </div>
        </motion.div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <AnimatePresence mode="wait">
            {activeTab === "refunds" ? (
              <motion.div
                key="refunds"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <PurchaseRefundsTable
                  refunds={paginated}
                  onDelete={handleDeleteRefund}
                />
              </motion.div>
            ) : (
              <motion.div
                key="credit-notes"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <PurchaseCreditNotesTable
                  creditNotes={paginated}
                  onDelete={handleDeleteCreditNote}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Empty state */}
        {filtered.length === 0 && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border mt-4"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon
                name={activeTab === "refunds" ? "RotateCcw" : "FileMinus"}
                size={32}
                className="text-primary"
              />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("purchaseRefunds.emptyTitle", {
                type:
                  activeTab === "refunds"
                    ? t("status.refunds")
                    : t("status.creditNotes"),
              })}
            </h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm
                ? t("common.adjustSearch")
                : activeTab === "refunds"
                  ? t("purchaseRefunds.refundEmpty")
                  : t("purchaseRefunds.creditNoteEmpty")}
            </p>
            {!searchTerm && (
              <Button
                variant="default"
                iconName="Plus"
                onClick={() =>
                  activeTab === "refunds"
                    ? navigate("/purchases/refunds/new")
                    : navigate("/purchases/credit-notes/new")
                }
              >
                {activeTab === "refunds"
                  ? t("purchaseRefunds.addRefund")
                  : t("purchaseRefunds.addCreditNote")}
              </Button>
            )}
          </motion.div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 px-6 py-4 bg-card rounded-xl border border-border flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t("common.showingRange", {
                from: (currentPage - 1) * itemsPerPage + 1,
                to: Math.min(currentPage * itemsPerPage, filtered.length),
                total: filtered.length,
                entity:
                  activeTab === "refunds"
                    ? t("status.refunds")
                    : t("status.creditNotes"),
              })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronLeft"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              />
              <span className="text-sm text-foreground px-2">
                {t("common.pageOf", {
                  current: currentPage,
                  total: totalPages,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                iconName="ChevronRight"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const PurchaseRefunds = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <PurchaseRefundsContent />
    </div>
  </SidebarProvider>
);

const LoadingSkeleton = ({ isCollapsed }) => (
  <div
    className={`transition-all duration-300 ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
  >
    <main className="p-4 md:p-6 animate-pulse">
      <div className="h-8 bg-muted rounded w-64 mb-2" />
      <div className="h-4 bg-muted rounded w-96 mb-8" />
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl" />
        ))}
      </div>
      <div className="h-12 bg-muted rounded-xl w-72 mb-6" />
      <div className="h-14 bg-muted rounded-xl mb-6" />
      <div className="bg-card rounded-xl border border-border p-6 space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-14 bg-muted rounded" />
        ))}
      </div>
    </main>
  </div>
);

export default PurchaseRefunds;
